if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.subscriptions = [];
        this.submitting = false;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';

        this.openButton = this.querySelector('[data-sticky-open]');
        this.confirmButton = this.querySelector('[data-sticky-confirm]');
        this.sheet = this.querySelector('[data-sticky-sheet]');
        this.overlay = this.querySelector('[data-sticky-overlay]');
        this.closeButton = this.querySelector('[data-sticky-close]');
        this.priceTarget = this.querySelector('[data-sticky-price]');
        this.buttonLabel = this.querySelector('[data-sticky-button-label]');

        this.openButton?.addEventListener('click', this.onOpen.bind(this));
        this.confirmButton?.addEventListener('click', this.onSubmit.bind(this));
        this.closeButton?.addEventListener('click', this.close.bind(this));
        this.overlay?.addEventListener('click', this.close.bind(this));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
        this.querySelectorAll('[data-sticky-pill]').forEach((pill) =>
          pill.addEventListener('click', this.onPillClick.bind(this))
        );

        this.setupObserver();

        this.subscriptions.push(
          subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onVariantSync.bind(this)),
          subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantSync.bind(this)),
          subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this)),
          subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this))
        );

        this.syncFromMainButton();
      }

      disconnectedCallback() {
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
        this.observer?.disconnect();
      }

      // Never cache form/inputs: product-info.js re-renders them on variant change.
      get mainForm() {
        return document.getElementById(this.productFormId);
      }

      get mainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      setupObserver() {
        // Shrink the viewport top by the sticky header height (+ a small buffer)
        // so the bar appears once the main button slips behind the fixed header,
        // not only when it passes the true viewport edge.
        const headerHeight =
          parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const topInset = headerHeight + 16;

        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            // Show once the main button is above the inset top edge (behind the header).
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < topInset;
            this.toggleVisible(scrolledPast);
          },
          { threshold: 0, rootMargin: `-${topInset}px 0px 0px 0px` }
        );
        this.observeTarget();
      }

      // Re-resolve the target each time: product-info.js can replace the
      // button/form nodes on variant change, detaching the observed element.
      observeTarget() {
        const target = this.mainButton || this.mainForm;
        if (!target) {
          requestAnimationFrame(() => this.observeTarget());
          return;
        }
        this.observer.disconnect();
        this.observer.observe(target);
      }

      toggleVisible(show) {
        if (show) this.hidden = false;
        requestAnimationFrame(() => this.classList.toggle('is-visible', show));
        // Lets CSS lift the scroll-to-top button above the bar (see CSS).
        document.body.classList.toggle('sticky-atc-visible', show);
      }

      onOpen() {
        if (this.isDisabled()) return;
        if (this.hasOnlyDefaultVariant) {
          this.onSubmit();
          return;
        }
        this.overlay.hidden = false;
        this.sheet.hidden = false;
        requestAnimationFrame(() => this.classList.add('sheet-open'));
        this.previousFocus = document.activeElement;
        const firstPill = this.sheet.querySelector('[data-sticky-pill]:not([aria-disabled="true"])');
        trapFocus(this.sheet, firstPill || this.sheet);
      }

      close() {
        if (!this.classList.contains('sheet-open')) return;
        this.classList.remove('sheet-open');
        removeTrapFocus(this.previousFocus);
        const raw = getComputedStyle(this).getPropertyValue('--duration-default').trim();
        const duration = (raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000) || 200;
        setTimeout(() => {
          this.sheet.hidden = true;
          this.overlay.hidden = true;
        }, duration);
      }

      onPillClick(event) {
        const pill = event.currentTarget;
        if (pill.getAttribute('aria-disabled') === 'true') return;

        const name = pill.dataset.optionName;
        const value = pill.dataset.value;

        const select = document.querySelector(
          `select[name="options[${CSS.escape(name)}]"][form="${this.productFormId}"]`
        );
        if (select) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          document
            .querySelectorAll(`input[type="radio"][form="${this.productFormId}"]`)
            .forEach((radio) => {
              if (radio.name.startsWith(`${name}-`) && radio.value === value) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
        }

        // Optimistic highlight; authoritative state arrives via PubSub sync.
        this.setActivePill(pill);
      }

      setActivePill(pill) {
        pill
          .closest('.sticky-atc__pills')
          .querySelectorAll('[data-sticky-pill]')
          .forEach((other) => {
            other.classList.remove('sticky-atc__pill--active');
            other.removeAttribute('aria-pressed');
          });
        pill.classList.add('sticky-atc__pill--active');
        pill.setAttribute('aria-pressed', 'true');
      }

      onSubmit() {
        if (this.isDisabled() || this.submitting) return;
        const form = this.mainForm;
        if (!form) return;
        this.submitting = true;
        this.confirmButton?.classList.add('loading');
        this.confirmButton?.querySelector('.loading__spinner')?.classList.remove('hidden');
        form.requestSubmit();
      }

      onVariantSync() {
        this.observeTarget();
        this.syncFromMainButton();
        this.syncPrice();
        this.syncActivePills();
      }

      onCartUpdate() {
        this.resetSubmit();
        this.close();
      }

      onCartError() {
        this.resetSubmit();
      }

      resetSubmit() {
        this.submitting = false;
        this.confirmButton?.classList.remove('loading');
        this.confirmButton?.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      isDisabled() {
        const button = this.mainButton;
        return (
          !button ||
          button.hasAttribute('disabled') ||
          button.getAttribute('aria-disabled') === 'true'
        );
      }

      syncFromMainButton() {
        const disabled = this.isDisabled();
        [this.openButton, this.confirmButton].forEach((button) => {
          if (button) button.toggleAttribute('aria-disabled', disabled);
        });
        // For single-variant products the bar button mirrors the main label
        // (Add to cart / Sold out / Unavailable).
        if (this.hasOnlyDefaultVariant && this.buttonLabel) {
          const label = this.mainButton?.querySelector('span')?.textContent.trim();
          if (label) this.buttonLabel.textContent = label;
        }
      }

      syncPrice() {
        const source = document.getElementById(`price-${this.sectionId}`);
        if (!source || !this.priceTarget) return;
        this.priceTarget.innerHTML = source.innerHTML;
        // The main price renders with price--large (2.75rem); the bar uses the
        // default size, so drop it to keep the synced price consistent.
        this.priceTarget.querySelector('.price')?.classList.remove('price--large');
      }

      syncActivePills() {
        this.querySelectorAll('.sticky-atc__option').forEach((fieldset) => {
          const pills = fieldset.querySelectorAll('[data-sticky-pill]');
          const name = pills[0]?.dataset.optionName;
          if (!name) return;

          let current = null;
          const select = document.querySelector(
            `select[name="options[${CSS.escape(name)}]"][form="${this.productFormId}"]`
          );
          if (select) {
            current = select.value;
          } else {
            const checked = Array.from(
              document.querySelectorAll(`input[type="radio"][form="${this.productFormId}"]:checked`)
            ).find((radio) => radio.name.startsWith(`${name}-`));
            current = checked?.value;
          }

          pills.forEach((pill) => {
            const active = pill.dataset.value === current;
            pill.classList.toggle('sticky-atc__pill--active', active);
            if (active) {
              pill.setAttribute('aria-pressed', 'true');
            } else {
              pill.removeAttribute('aria-pressed');
            }
          });
        });
      }
    }
  );
}
